import React from "react";
import {UserPermissionEnum} from "../../../utils/types/user";

import {
  CompareDateLabels,
  getAggregationValues,
  hasMorePages,
  ReportProps,
  useServerSort,
  hasAdmissionDate,
} from "./Report.common";
import {
  ReportAdmission,
  ReportAggregationTypes,
  ReportsType,
  SortField,
} from "../../../store/reporting-reports/reporting-reports.common";
import {CardTitle} from "../../../atoms/card-title";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContextReports} from "../ReportingContextReports";
import {withCompare} from "./helpers";
import {Aggregation} from "../../../atoms/admin-reporting/atoms/aggregation";
import {LoadMoreButton} from "../atoms/load-more-button";
import {
  Column,
  ListTableReporting,
} from "../../../atoms/admin-reporting/list-table-reporting";
import {
  DateReportsCell,
  IconCheckCell,
  TextCell,
  TextCellEllipsis,
} from "../../../atoms/list-table/SharedCells";

export const LAUNDRY_REPORT_ID = "laundry" as const;

export const ReportLaundry = (props: ReportProps) => {
  const [sortDirection, sortField, setSort] = useServerSort(
    SortField.facilityName,
  );

  return (
    <>
      <div className="chart-header">
        <CardTitle title="Request Laundry" subtitle="by Admissions" />
        <CompareDateLabels dateRange={props.dateRange} />
      </div>

      <RequiredUserPermission permission={UserPermissionEnum.LAUNDRY_CONSENT}>
        <ReportingContextReports
          dataPaths={withCompare({
            type: ReportsType.laundry,
            query: {
              from: props.dateRange.from,
              to: props.dateRange.to,
              sortDirection: sortDirection,
              sortField: sortField,
              facilities: props.facilities,
            },
          })}
          enabled={props.reportingBlockSetters.enabled}
          onDataPathChange={
            props.reportingBlockSetters
              ? props.reportingBlockSetters.setExportDataPath
              : undefined
          }
        >
          {(d, loadMore) => (
            <>
              <div className="aggregation-row">
                <Aggregation
                  reports
                  subject="AGREE"
                  values={getAggregationValues(
                    d,
                    ReportAggregationTypes.positiveAnswersTotal,
                  )}
                />
                <Aggregation
                  reports
                  subject="NOT AGREE"
                  values={getAggregationValues(
                    d,
                    ReportAggregationTypes.negativeAnswersTotal,
                  )}
                />
              </div>

              <ListTableReporting
                data={d}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={setSort}
                noRowsMessage="No Admissions found with these parameters..."
                full={props.reportingBlockSetters.isFullReport}
              >
                <Column<ReportAdmission>
                  sortKey={SortField.residentName}
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.residentName}
                    </TextCellEllipsis>
                  )}
                  label="Resident"
                  width={28}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.facilityName}
                    </TextCellEllipsis>
                  )}
                  label="Facility"
                  sortKey={SortField.facilityName}
                  width={20}
                />

                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <TextCell small>{row.rowData.roomNumber}</TextCell>
                  )}
                  label="Room Number"
                  sortKey={SortField.roomNumber}
                  width={14}
                />
                {hasAdmissionDate(d) && (
                  <Column<ReportAdmission>
                    cellRenderer={row => (
                      <DateReportsCell data={row.rowData.admissionDate} />
                    )}
                    label="Admission Date"
                    sortKey={SortField.admissionDate}
                    width={13}
                  />
                )}
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.createdDate} />
                  )}
                  label="Created Date"
                  sortKey={SortField.createdDate}
                  width={14}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.updatedDate} />
                  )}
                  label="Last Update Date"
                  sortKey={SortField.updatedDate}
                  width={14}
                />

                <Column<ReportAdmission>
                  cellRenderer={(row: any) => (
                    <DateReportsCell data={row.rowData.sentToResidentDate} />
                  )}
                  label="Sent to Resident Date"
                  sortKey={SortField.sentToResidentDate}
                  width={14}
                />

                <Column<ReportAdmission>
                  cellRenderer={row => <></>}
                  width={6}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <IconCheckCell accepted={row.rowData.answerIsPositive} />
                  )}
                  label="Yes"
                  sortKey={SortField.answerIsPositive}
                  width={5}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <IconCheckCell accepted={!row.rowData.answerIsPositive} />
                  )}
                  label="No"
                  sortKey={SortField.answerIsNegative}
                  width={5}
                />
              </ListTableReporting>
              <LoadMoreButton
                hasMorePages={hasMorePages(
                  d,
                  props.reportingBlockSetters.isFullReport,
                )}
                onClick={loadMore}
              />
            </>
          )}
        </ReportingContextReports>
      </RequiredUserPermission>
    </>
  );
};
